<template>
  <v-dialog
    :value="value"
    max-width="600"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title>
        グループ編集
      </v-card-title>

      <v-card-text>
        <!-- グループ名 -->
        <div class="mb-4">
          <div class="text-subtitle-2 mb-2">
            グループ名
          </div>
          <div class="d-flex align-center">
            <v-text-field
              v-model="editName"
              dense
              outlined
              hide-details
              class="flex-grow-1 mr-2"
            />
            <v-btn
              color="primary"
              :disabled="!isNameChanged"
              :loading="isUpdatingName"
              @click="updateName"
            >
              変更
            </v-btn>
          </div>
        </div>

        <!-- 割り当て済み権限 -->
        <div class="mb-4">
          <div class="text-subtitle-2 mb-2">
            割り当て済み権限
          </div>
          <div v-if="assignedPermissions.length > 0">
            <v-chip
              v-for="permission in assignedPermissions"
              :key="permission.id"
              small
              close
              class="ma-1"
              @click:close="removePermission(permission)"
            >
              {{ getSubjectLabel(permission.subject) }} : {{ permission.action }}
            </v-chip>
          </div>
          <div
            v-else
            class="text-body-2 grey--text"
          >
            権限がありません
          </div>
        </div>

        <!-- 権限追加 -->
        <div>
          <div class="text-subtitle-2 mb-2">
            権限を追加
          </div>
          <div class="d-flex align-center">
            <v-select
              v-model="selectedPermissionId"
              :items="availablePermissions"
              item-text="label"
              item-value="id"
              placeholder="権限を選択..."
              dense
              outlined
              hide-details
              class="flex-grow-1 mr-2"
            />
            <v-btn
              color="primary"
              :disabled="!selectedPermissionId"
              :loading="isAdding"
              @click="addPermission"
            >
              追加
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="$emit('input', false)"
        >
          閉じる
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'

export default {
  name: 'EditGroupDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    group: {
      type: Object,
      default: null,
    },
    permissions: {
      type: Array,
      required: true,
    },
    subjectLabels: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const selectedPermissionId = ref(null)
    const isAdding = ref(false)
    const isRemoving = ref(false)
    const editName = ref('')
    const isUpdatingName = ref(false)

    const isNameChanged = computed(() => {
      return props.group && editName.value.trim() !== '' && editName.value.trim() !== props.group.name
    })

    const updateName = async () => {
      if (!isNameChanged.value) return
      isUpdatingName.value = true
      emit('update-name', {
        groupId: props.group.id,
        name: editName.value.trim(),
      })
      isUpdatingName.value = false
    }

    const getSubjectLabel = subject => props.subjectLabels[subject] || subject

    const assignedPermissions = computed(() => {
      if (!props.group) return []

      return props.group.permissions || []
    })

    const availablePermissions = computed(() => {
      if (!props.group) return []
      const assignedIds = assignedPermissions.value.map(p => p.id)

      return props.permissions
        .filter(p => !assignedIds.includes(p.id))
        .map(p => ({
          id: p.id,
          label: `${getSubjectLabel(p.subject)} : ${p.action}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ja'))
    })

    const addPermission = async () => {
      if (!selectedPermissionId.value || !props.group) return
      isAdding.value = true

      emit('add', {
        groupId: props.group.id,
        permissionId: selectedPermissionId.value,
      })

      selectedPermissionId.value = null
      isAdding.value = false
    }

    const removePermission = async permission => {
      if (!props.group) return

      emit('remove', {
        groupId: props.group.id,
        permissionId: permission.id,
      })
    }

    // Reset selection when dialog opens/closes
    watch(
      () => props.value,
      newVal => {
        if (newVal && props.group) {
          editName.value = props.group.name || ''
        } else {
          selectedPermissionId.value = null
        }
      },
    )

    return {
      selectedPermissionId,
      isAdding,
      isRemoving,
      editName,
      isUpdatingName,
      isNameChanged,
      updateName,
      assignedPermissions,
      availablePermissions,
      addPermission,
      removePermission,
      getSubjectLabel,
    }
  },
}
</script>
